import { Button, Grid, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import TagsFilter from '../TagFilter/TagsFilter';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import SavedSearch, { ComponentType } from '../SavedSearch/SavedSearch';
import Swal from 'sweetalert2';
import SavedSearchApi, { SaveSavedSearchParam } from '../SavedSearch/api/SavedSearchApi';
import SavedSearchModel from '../SavedSearch/models/SavedSearchModel';
import { addSavedSearch, savedSearchSelector } from '../SavedSearch/reducers/SavedSearchReducer';
import { QueryItem } from '../Tonnage/models/TonnageQueryModel';
import CompanyNamePICApi, { SearchCompanyNamePICParam } from './api/CompanyNamePICApi';
import CompanyNamePICModel from './models/CompanyNamePICModel';
import { CompanyNamePICSearchSelector, resetCompanyNamePICSearch, setCompanyNamePICs, setCompanyNames, setEmails, setPics, setSearchResultTotalRecord, setTriggerCompanyNamePICSearch, setTriggerFocusCompanyName } from './reducers/CompanyNamePICSearchReducer';
import { CompanyNamePICSelectorSelector, setCompanyNamePICSearchResults } from '../CompanyNamePICSelector/reducers/CompanyNamePICSelectorReducer';

const CompanyNamePICSearch: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const companyNamePICSearchData = useSelector(CompanyNamePICSearchSelector);
    const savedSearchData = useSelector(savedSearchSelector);
    const companyNamePICSelectorData = useSelector(CompanyNamePICSelectorSelector);

    useEffect(() => {
        if (companyNamePICSelectorData.searchResults.length > 0) {
            return;
        }

        dispatch(showLoading(true));
        CompanyNamePICApi.GetAll()
            .then((response: BaseResponse<CompanyNamePICModel[]>) => {
                if (response.success) {
                    dispatch(setCompanyNamePICSearchResults(response.data));
                    dispatch(setCompanyNamePICs(response.data));
                    dispatch(setSearchResultTotalRecord(response.data.length));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            })
    }, []);

    const saveSavedSearch = () => {
        Swal.fire({
            title: 'Provide Saved Search Name',
            input: 'text',
            inputAttributes: {
                autocapitalize: 'off'
            },
            showCancelButton: true,
            confirmButtonText: 'Sumbit',
            showLoaderOnConfirm: true,
            allowOutsideClick: false,
            confirmButtonColor: '#1976d2',
            preConfirm: (name) => {
                if (!name || name.length === 0) {
                    Swal.showValidationMessage("Name ")
                }
            },
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));

                SavedSearchApi.Save({
                    name: result.value ? result.value : "",
                    component: ComponentType.CompanyNamePIC,
                    queryJson: JSON.stringify(companyNamePICSearchData.query)
                } as SaveSavedSearchParam)
                    .then((response: BaseResponse<SavedSearchModel>) => {
                        if (response.success) {
                            dispatch(addSavedSearch(response.data));
                            Utility.Alert.Success("Save Successful");
                        } else {
                            Utility.Alert.Error(response.message);
                        }
                    })
                    .catch((error) => {
                        Utility.Alert.Error(error);
                    })
                    .finally(() => {
                        dispatch(showLoading(false));
                    });
            }
        })
    }

    useEffect(() => {
        if (companyNamePICSearchData.triggerSearch === 0) {
            return;
        }

        dispatch(showLoading(true));

        CompanyNamePICApi.Search({
            query: companyNamePICSearchData.query
        } as SearchCompanyNamePICParam)
            .then((response: BaseResponse<CompanyNamePICModel[]>) => {
                if (response.success) {
                    dispatch(setCompanyNamePICs(response.data));
                    dispatch(setSearchResultTotalRecord(response.totalRecord));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    }, [companyNamePICSearchData.triggerSearch])

    const getTonnageSearchClass = () => {
        let className = "nice-scroll ";
        if (savedSearchData.expanded) {
            className += "tonnage-search-wrapper";
        } else {
            className += "tonnage-search-wrapper-saved-search-hide";
        }

        return className;
    }

    return (
        <>
            <SavedSearch
                component={ComponentType.CompanyNamePIC}
            >
            </SavedSearch>
            <CustomAccordion
                title="Search"
                defaultOpen={true}
                contentClassName={getTonnageSearchClass()}>
                <div className='tonnage-search-body'>
                    <Grid container>
                        <Grid item xs={8}>
                            <Grid container>
                                <Grid xs={8}>
                                    <Tooltip title="Alt + W">
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            className="tonnage-search-save-button"
                                            onClick={() => {
                                                dispatch(setTriggerCompanyNamePICSearch((new Date()).getTime()));
                                            }}
                                        >
                                            RUN SEARCH (w)
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid xs={4}>
                                    <Tooltip title="Reset (Alt + Q)">
                                        <Button
                                            className="clear-input-button"
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                dispatch(resetCompanyNamePICSearch());
                                                dispatch(setTriggerFocusCompanyName(new Date().getTime()));
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>

                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => {
                                    saveSavedSearch();
                                }}
                            >
                                Save Search
                            </Button>
                        </Grid>
                    </Grid>
                    <TagsFilter
                        data={_.map(_.groupBy(companyNamePICSelectorData.searchResults, (item) => item.companyName), (companyNameGroup) => {
                            return {
                                id: companyNameGroup[0].companyName,
                                value: companyNameGroup[0].companyName,
                                positive: true
                            } as QueryItem
                        })}
                        useFreeText={true}
                        label="Company Name"
                        className="tonnage-search-item"
                        value={companyNamePICSearchData.query.companyNames}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCompanyNames(value));
                        }}
                        triggerFocusInput={companyNamePICSearchData.triggerFocusCompanyName}
                    ></TagsFilter>
                    <TagsFilter
                        data={[]}
                        useFreeText={true}
                        label="PIC"
                        className="tonnage-search-item"
                        value={companyNamePICSearchData.query.pics}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setPics(value));
                        }}
                    ></TagsFilter>
                    <TagsFilter
                        data={[]}
                        useFreeText={true}
                        label="Email"
                        className="tonnage-search-item"
                        value={companyNamePICSearchData.query.emails}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setEmails(value));
                        }}
                    ></TagsFilter>
                </div>
            </CustomAccordion>
        </>
    )
};

export default CompanyNamePICSearch;